import React from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { zodResolver } from '@hookform/resolvers/zod';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { GeneralASError } from 'src/registry/types/axios';
import { generalMessages } from 'src/registry/utils/messages';
import { cn } from 'src/utils';
import { ICON_MAP } from 'src/utils/constants';
import { useOnError } from 'src/utils/errors';
import SubscriptionItemPricingOverrideForm from 'src/features/subscriptionItemPricingOverrides/components/forms/SubscriptionItemPricingOverrideForm';
import {
    subscriptionItemPricingOverrideFormSchema,
    SubscriptionItemPricingOverrideFormSchemaValues,
} from 'src/features/subscriptionItemPricingOverrides/utils/formSchema';
import { SubscriptionItemPricingOverride } from 'src/features/subscriptionItemPricingOverrides/utils/types';

interface SubscriptionItemPricingOverrideFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedPricingOverride?: SubscriptionItemPricingOverride;
    children?: React.ReactNode;
    isPending: boolean;
    icon: IconProp;
    title: string;
    subtitle: string;
    mutate: (
        data: SubscriptionItemPricingOverrideFormSchemaValues,
        onSuccess: () => void,
        onError: (error: GeneralASError) => void,
    ) => void;
}

function SubscriptionItemPricingOverrideFormModal<TData>({
    selectedPricingOverride,
    isPending,
    children,
    className,
    icon,
    title,
    subtitle,
    mutate,
}: SubscriptionItemPricingOverrideFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const onError = useOnError();

    const defaultValues = React.useMemo(
        () => ({
            valid_from: selectedPricingOverride?.valid_from ?? '',
            valid_to: selectedPricingOverride?.valid_to ?? undefined,
            coefficient: selectedPricingOverride?.coefficient ?? '',
        }),
        [selectedPricingOverride],
    );

    const form = useForm<SubscriptionItemPricingOverrideFormSchemaValues>({
        resolver: zodResolver(subscriptionItemPricingOverrideFormSchema),
        defaultValues,
    });

    React.useEffect(() => {
        form.reset(defaultValues);
    }, [form, isModalOpen, defaultValues]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();
        setIsModalOpen(isOpen);
    };

    const onSubmit = React.useCallback(
        (values: SubscriptionItemPricingOverrideFormSchemaValues) => {
            mutate(
                values,
                () => setIsModalOpen(false),
                (error) => onError(error, form.setError),
            );
        },
        [form.setError, mutate, onError],
    );

    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children ? (
                <DialogTrigger asChild>{children}</DialogTrigger>
            ) : (
                <DialogTrigger asChild>
                    <Button className={cn('ml-auto', className)}>
                        <FontAwesomeIcon icon={ICON_MAP.ADD} />
                        {title}
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={icon} />
                        <div>
                            <DialogTitle>{title}</DialogTitle>
                            <DialogDescription>{subtitle}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <SubscriptionItemPricingOverrideForm form={form} onSubmit={onSubmit} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending || (!!selectedPricingOverride && !form.formState.isDirty)}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {title}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default SubscriptionItemPricingOverrideFormModal;
